import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {
  mobileOnlyLinaria,
  tabletAndUpLinaria,
} from '../../../constants/customMedia';
import {overflowEllipsis} from '../../../constants/ellipsis';
import {FONT_SIZES} from '../../../constants/typography';
import {ImageType} from '../../../types/Image';
import {Button} from '../../atoms/Buttons/Button';
import {PixelIcon} from '../../atoms/Icon/PixelIcon';
import {Text} from '../../atoms/Text/Text';

export interface SustainabilityInfoContent {
  className?: string;
  headline?: string;
  subline?: string;
  cluster: {
    name: string;
    icon: ImageType;
    attributes: Array<{label: string; text: string}>;
    description: string;
  };
  certificate?: {
    name: string;
    icon?: ImageType;
    licence?: string;
    headline?: string;
  };
  cta: {
    label: string;
    linkTarget: {url: string};
  };
  hideBorders?: boolean;
  showCTAWithCloseButton?: boolean;
  onClose?: VoidFunction;
}

export const SustainabilityInfoContent: React.FC<SustainabilityInfoContent> = ({
  headline,
  subline,
  cluster,
  certificate,
  cta,
  hideBorders = false,
  showCTAWithCloseButton = false,
  onClose,
  ...rest
}) => {
  const {t} = useTranslation();

  return (
    <Wrapper onClick={(e: React.MouseEvent) => e.stopPropagation()} {...rest}>
      {headline && (
        <Headline>
          <Title size="px16" weight="mediumHeavy" as="p" textAlignment="center">
            {headline}
          </Title>

          <ProductName
            size="px12"
            weight="mediumBold"
            as="p"
            textAlignment="center"
          >
            {subline}
          </ProductName>
        </Headline>
      )}

      {(cluster.attributes.length || cluster.description) && (
        <Section
          $hideBorders={hideBorders}
          data-testid="sustainability-ClusterSection"
        >
          {cluster.attributes.map((attribute, index) => (
            <SustainabilityAttribute key={index}>
              <Text weight="mediumBold">{attribute.label}</Text>
              <Text weight="mediumBold" color="Grey1">
                {attribute.text}
              </Text>
            </SustainabilityAttribute>
          ))}

          {cluster.description && (
            <Description
              weight="mediumBold"
              as="p"
              $withMarginTop={cluster.attributes.length > 0}
            >
              {cluster.description}
            </Description>
          )}
        </Section>
      )}

      {certificate && (
        <Section
          $hideBorders={hideBorders}
          data-testid="sustainability-CertificatesSection"
        >
          {certificate.headline && (
            <CertificateHeadline weight="mediumHeavy">
              {certificate.headline}
            </CertificateHeadline>
          )}
          <SustainabilityAttributeWithIcon>
            {certificate.icon && (
              <StyledPixelIcon imageUrl={certificate.icon.url} />
            )}

            <CertificateNameAndLicence>
              <Text weight="mediumBold">{certificate.name}</Text>
              {certificate.licence && (
                <Text weight="mediumBold" size="px12">
                  {certificate.licence}
                </Text>
              )}
            </CertificateNameAndLicence>
          </SustainabilityAttributeWithIcon>
        </Section>
      )}

      {showCTAWithCloseButton && (
        <CTASection>
          <CloseButton
            variant="secondary"
            onClick={onClose}
            data-testid="sustainability-CloseButton"
            aria-label={undefined}
            size="fit-width"
          >
            <Text weight="mediumHeavy">{t('Close')}</Text>
          </CloseButton>
          <CTALink
            variant="primary"
            href={cta.linkTarget.url}
            data-testid="sustainability-LearnMoreButton"
            size="full-width"
          >
            {cta.label}
          </CTALink>
        </CTASection>
      )}

      {!showCTAWithCloseButton && (
        <LearnMoreButton
          variant="secondary"
          href={cta.linkTarget.url}
          aria-label={undefined}
          size="fit-width"
        >
          <Text weight="mediumHeavy" color="Black">
            {cta.label}
          </Text>
        </LearnMoreButton>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  line-height: ${FONT_SIZES.px20};
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const Section = styled.div<{
  $hideBorders?: boolean;
}>`
  border-bottom: ${props =>
    props.$hideBorders ? 'unset' : `1px solid ${COLORS.Grey4}`};
`;

const Headline = styled(Section)`
  ${mobileOnlyLinaria(`
    padding-top: 0;
  `)}
`;

const SustainabilityAttribute = styled.div`
  & > *:first-child {
    margin-right: 6px;
  }
`;

const SustainabilityAttributeWithIcon = styled(SustainabilityAttribute)`
  display: flex;
  align-items: center;
`;

const Title = styled(Text)`
  ${mobileOnlyLinaria('margin: 0;')}
`;

const ProductName = styled(Text)`
  margin-top: 6px;
  margin-bottom: 0;
`;

const Description = styled(Text)<{$withMarginTop: boolean}>`
  margin-top: ${props => (props.$withMarginTop ? '1rem' : 0)};
`;

const CTASection = styled(Section)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: none;
  gap: 8px;

  ${mobileOnlyLinaria(`
    border-top: 1px solid ${COLORS.Grey4};
  `)}
`;

const CloseButton = styled(Button)`
  padding: 17px 25px;
`;

const LearnMoreButton = styled(Button)`
  && {
    margin-left: 1px;
  }
`;

const CTALink = styled(Button)`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;

  &&&& {
    padding: 16px;
    ${overflowEllipsis}
  }
`;

const CertificateHeadline = styled(Text)`
  margin-bottom: 0.625rem;
  display: inline-block;
`;

const CertificateNameAndLicence = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledPixelIcon = styled(PixelIcon)`
  --size: 56px;
  ${tabletAndUpLinaria(`--size: 64px;`)}
  background-position: center;

  &&& {
    width: var(--size);
    height: var(--size);
    flex-basis: var(--size);
  }
`;
